import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../auth'
import { Book } from '../../content'
import { Premium } from '../../membership'
import { ReadingSession } from './reading-session'

// =============================================================================
// READING SESSION SUMMARY - Aggregated data (computed by background job)
// =============================================================================

export const ReadingSessionSummary = pgTable(
  'reading_session_summary',
  {
    id: uuid().primaryKey().defaultRandom(),
    readingSessionId: uuid()
      .references(() => ReadingSession.id, { onDelete: 'cascade' })
      .unique()
      .notNull(),
    bookId: uuid()
      .references(() => Book.id, { onDelete: 'cascade' })
      .notNull(),
    userId: text().references(() => user.id, { onDelete: 'set null' }),
    premiumId: uuid().references(() => Premium.id, { onDelete: 'restrict' }),

    // Aggregated metrics (computed from PageReadingActivity)
    totalDurationSeconds: integer().default(0).notNull(),
    totalPagesRead: integer().default(0).notNull(),
    uniquePagesRead: integer().default(0).notNull(),
    lastPageNumber: integer(),
    firstActiveAt: timestamp({ mode: 'date', withTimezone: true }),
    lastActiveAt: timestamp({ mode: 'date', withTimezone: true }),

    // Session state
    isCompleted: boolean().default(false),
    completedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Timestamps
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (t) => [
    index('idx_rss_user_book').on(t.userId, t.bookId),
    index('idx_rss_last_active').on(t.lastActiveAt),
    // Live "Lanjut Baca" / reading history: latest session per (user, book) — continueReadingLive, computeReadingHistoryDaily.
    index('idx_rss_user_book_last_active').on(t.userId, t.bookId, t.lastActiveAt.desc()),
    /**
     * The monthly royalty cron joins every qualifying summary to `premium` through this column and
     * counts distinct seats off it, so the column was being scanned in full each run. It is also the
     * referencing side of an `ON DELETE restrict` foreign key, which Postgres does not index for you —
     * every attempt to delete a seat scanned this table too.
     */
    index('idx_rss_premium').on(t.premiumId),
  ],
)

export const readingSessionSummaryRelations = relations(ReadingSessionSummary, ({ one }) => ({
  readingSession: one(ReadingSession, {
    fields: [ReadingSessionSummary.readingSessionId],
    references: [ReadingSession.id],
  }),

  book: one(Book, {
    fields: [ReadingSessionSummary.bookId],
    references: [Book.id],
  }),

  user: one(user, {
    fields: [ReadingSessionSummary.userId],
    references: [user.id],
  }),

  premium: one(Premium, {
    fields: [ReadingSessionSummary.premiumId],
    references: [Premium.id],
  }),
}))

export const InsertReadingSessionSummarySchema = createInsertSchema(ReadingSessionSummary).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export const UpdateReadingSessionSummarySchema = createUpdateSchema(ReadingSessionSummary).partial().omit({
  id: true,
  readingSessionId: true,
  bookId: true,
  userId: true,
  premiumId: true,
  createdAt: true,
  updatedAt: true,
})
export const SelectReadingSessionSummarySchema = createSelectSchema(ReadingSessionSummary)

export type InsertReadingSessionSummary = typeof ReadingSessionSummary.$inferInsert
export type UpdateReadingSessionSummary = z.infer<typeof UpdateReadingSessionSummarySchema>
export type SelectReadingSessionSummary = typeof ReadingSessionSummary.$inferSelect
